import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import KpiCard from '../../components/KpiCard';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { formatTRY, formatSayi, formatCompact } from '../../utils/analytics/format';
import { exportCsv } from '../../utils/analytics/export';
import { AY_ADLARI_KISA } from '../../constants/tarih';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const reasonColors = {
  'Stoklama Hatası': '#6B7280',
  'Mutfak / Hazırlık Hatası': '#3B82F6',
  'Servis Hatası': '#F97316',
  'Diğer': '#1E3A8A',
};

const ZayiRaporuPage = () => {
  useAuth();

  const [data, setData] = useState(null);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtreler
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [baslangic, setBaslangic] = useUrlState('baslangic', new Date(today.getFullYear(), today.getMonth() - 5, 1).toISOString().split('T')[0]);
  const [bitis, setBitis] = useUrlState('bitis', today.toISOString().split('T')[0]);
  const [lokasyonId, setLokasyonId] = useUrlState('lokasyon', '');

  const setQuickRange = (months) => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth() - months + 1, 1);
    setBaslangic(start.toISOString().split('T')[0]);
    setBitis(end.toISOString().split('T')[0]);
  };

  const setThisMonth = () => {
    setBaslangic(firstOfMonth.toISOString().split('T')[0]);
    setBitis(today.toISOString().split('T')[0]);
  };

  const setLastMonth = () => {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    setBaslangic(start.toISOString().split('T')[0]);
    setBitis(end.toISOString().split('T')[0]);
  };

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_URL}/api/raporlar/zayi?tarih_baslangic=${baslangic}&tarih_bitis=${bitis}`;
      if (lokasyonId) url += `&lokasyon_id=${lokasyonId}`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        setData(await res.json());
      } else {
        const e = await res.json().catch(() => ({}));
        setError(e.detail || `Rapor yüklenemedi (${res.status})`);
      }
    } catch (err) {
      logError('Zayi raporu yüklenemedi:', err);
      setError('Sunucu hatası — bağlantıyı kontrol edin');
    } finally {
      setLoading(false);
    }
  }, [baslangic, bitis, lokasyonId]);

  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setLokasyonlar(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  // Bar chart component
  // colorKey: opsiyonel — her bar kendi rengini item[colorKey]'den alır (master
  // data kategori rengi). Yoksa tek `color` prop'una düşer.
  const HorizontalBarChart = ({ items, valueKey, labelKey, color, colorMap }) => {
    if (!items || items.length === 0) return <div style={{ color: colors.textTertiary, fontSize: '13px', padding: '20px', textAlign: 'center' }}>Veri yok</div>;
    const maxVal = Math.max(...items.map(i => i[valueKey] || 0));
    if (maxVal === 0) return <div style={{ color: colors.textTertiary, fontSize: '13px', padding: '20px', textAlign: 'center' }}>Veri yok</div>;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map((item, idx) => {
          const val = item[valueKey] || 0;
          const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
          const barColor = colorMap?.[item[labelKey]] || color;
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '120px', fontSize: '12px', color: '#555', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item[labelKey]}>
                {item[labelKey]}
              </div>
              <div style={{ flex: 1, background: '#f0f0f0', borderRadius: radius.sm, height: '24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`, height: '100%',
                  background: `linear-gradient(to right, ${barColor}, ${barColor}cc)`,
                  borderRadius: radius.sm, transition: 'width 0.5s ease',
                  minWidth: pct > 0 ? '2px' : '0'
                }} />
              </div>
              <div style={{ width: '80px', fontSize: '12px', fontWeight: '600', color: '#333', flexShrink: 0 }}>
                {formatTRY(val)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Trend bar chart (vertical)
  const TrendChart = ({ items }) => {
    if (!items || items.length === 0) return <div style={{ color: colors.textTertiary, fontSize: '13px', padding: '20px', textAlign: 'center' }}>Veri yok</div>;
    const maxVal = Math.max(...items.map(i => i.toplam_deger || 0));
    if (maxVal === 0) return <div style={{ color: colors.textTertiary, fontSize: '13px', padding: '20px', textAlign: 'center' }}>Veri yok</div>;

    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', height: '180px', padding: '10px 0' }}>
        {items.map((item, idx) => {
          const val = item.toplam_deger || 0;
          const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
          const parts = item.ay.split('-');
          const ayLabel = AY_ADLARI_KISA[parts[1]] || parts[1];
          return (
            <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: '11px', color: '#555', marginBottom: '4px', fontWeight: '600' }}>
                {formatCompact(val)}
              </div>
              <div style={{
                width: '100%', height: `${pct}%`,
                background: `linear-gradient(to top, ${styles.danger}, #ff8a80)`,
                borderRadius: '4px 4px 0 0', minHeight: '4px',
                transition: 'height 0.5s ease'
              }} />
              <div style={{ fontSize: '10px', color: '#888', marginTop: '6px' }}>{ayLabel}</div>
              <div style={{ fontSize: '9px', color: '#aaa' }}>{parts[0]}</div>
            </div>
          );
        })}
      </div>
    );
  };

// **********************   EKLEME   ***************************

const FireZayiMaliyetChart = ({ rows, productsSummary }) => {
  if (!rows || rows.length === 0) {
    return <div style={{ color: colors.textTertiary, fontSize: '13px', padding: '20px', textAlign: 'center' }}>Veri yok</div>;
  }

  // Neden kategorileri ve renkleri master-data'dan türetilir (hardcode YOK):
  // her detay satırı neden_kategori_ad + neden_kategori_renk taşır; kategorisiz/
  // legacy kayıtlar "Belirtilmedi" kovasına düşer ve sıralı yedek paletten beslenir.
  const getReason = (r) => r.neden_kategori_ad || r.neden || 'Belirtilmedi';

  const reasonColorMap = {};
  rows.forEach((r) => {
    const reason = getReason(r);
    if (!(reason in reasonColorMap)) reasonColorMap[reason] = r.neden_kategori_renk || null;
  });
  const reasons = Object.keys(reasonColorMap);

  const products = [...new Set(rows.map(r => r.malzeme_ad).filter(Boolean))]
    .map(product => {
      const productRows = rows.filter(r => r.malzeme_ad === product);
      
      const summaryItem = productsSummary?.find(x => x.malzeme_ad === product);
      const totalCost = Number(summaryItem?.toplam_deger) || productRows.reduce((sum, r) => sum + (Number(r.deger) || 0), 0);
      
      const totalQty = productRows.reduce((sum, r) => sum + (Number(r.miktar) || 0), 0);

      const parts = reasons.map(reason => {
        const qty = productRows
          .filter(r => getReason(r) === reason)
          .reduce((sum, r) => sum + (Number(r.miktar) || 0), 0);

        return {
          reason,
          qty,
          // percent: totalQty > 0 ? (qty / totalQty) * 12 : 0
          percent: totalQty > 0 ? (qty / totalQty) * 100 : 0
          //
        };
      });

      return { product, totalCost, totalQty, parts };
    })
    .sort((a, b) => b.totalCost - a.totalCost)
    .slice(0, 8);

  const maxCost = Math.max(...products.map(p => p.totalCost), 1);
  
  const costAxisMax = Math.ceil(maxCost / 1000) * 1000;
  const costTicks = Array.from(
      { length: costAxisMax / 1000 + 1 },
      (_, i) => costAxisMax - i * 1000
    );
  
  const maxLeft = 100;
  const chartHeight = 360;
  // Maliyet (TL) barı: saf kırmızı, master-data "Servis Hatası" magentasıyla
  // çakışıyordu (ikisi de sıcak/kırmızımsı, yan yana ayırt edilmiyordu). Nötr koyu
  // slate — sıcak kategori renklerinin hiçbiriyle karışmaz, "toplam maliyet"
  // çapasını net vurgular.
  const costColor = '#F72E05';

  return (
    <div>

      <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr 60px', gap: '10px', alignItems: 'end' }}>
        <div style={{ height: chartHeight, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '12px', color: '#444' }}>
          {/* {[12, 10, 8, 6, 4, 2, 0].map(v => <div key={v}>{v}</div>)} */}
          
          {[maxLeft, maxLeft * 0.8, maxLeft * 0.6, maxLeft * 0.4, maxLeft * 0.2, 0].map(v => (
  <div key={v}>{Math.round(v)}</div>
))}
   {/* ? */}      
        </div>

        <div style={{ position: 'relative', height: chartHeight, borderBottom: '1px solid #555' }}>
         {/*  {[0, 2, 4, 6, 8, 10, 12].map(v => ( */} 
          
          {[0, maxLeft * 0.2, maxLeft * 0.4, maxLeft * 0.6, maxLeft * 0.8, maxLeft].map(v => (
            <div
              key={v}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: `${(v / maxLeft) * 100}%`,
                borderTop: '1px solid #ddd'
              }}
            />
          ))}

          <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around' }}>
            {products.map((p, idx) => {
              let bottom = 0;

              return (
                <div key={p.product} style={{ width: `${100 / products.length}%`, display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'flex-end' }}>
                  <div style={{ width: '34px', height: chartHeight, position: 'relative' }}>
                    {p.parts.map((part, i) => {
                      const h = (part.percent / maxLeft) * chartHeight;
                      const currentBottom = bottom;
                      bottom += h;

                      return (
                        <div
                          key={part.reason}
                          title={`${part.reason}: ${formatSayi(part.percent)}`}
                          style={{
                            position: 'absolute',
                            bottom: currentBottom,
                            height: h,
                            left: 0,
                            right: 0,

                            background: reasonColors[part.reason] || '#9CA3AF'
                          }}
                        />
                      );
                    })}
                  </div>

                  <div
                    title={`${p.product}: ${formatTRY(p.totalCost)}`}
                    style={{
                      width: '34px',
                      height: `${(p.totalCost / maxCost) * chartHeight}px`,
                      background: costColor
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ height: chartHeight, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '12px', color: '#444' }}>
          {costTicks.map((v, i) => (
            <div key={i}>{v === 0 ? '0' : `${v / 1000}k`}</div>
          ))}
        </div>
        
      </div>

      <div style={{ display: 'flex', marginLeft: '60px', marginRight: '70px', marginTop: '8px', justifyContent: 'space-around', fontSize: '12px', color: '#444' }}>
        {products.map((p, i) => (
          <div key={p.product} title={p.product} style={{ width: `${100 / products.length}%`, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {p.product}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '18px', flexWrap: 'wrap', marginTop: '34px', fontSize: '12px', color: '#444' }}>
        <div><span style={{ display: 'inline-block', width: 12, height: 12, background: costColor, marginRight: 6 }} />Maliyet</div>
        {reasons.map((reason, i) => (
          <div key={reason}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: reasonColors[reason] || '#9CA3AF', marginRight: 6 }} />
            % {reason}
          </div>
        ))}
      </div>
    </div>
  );
};
  const exportCSV = () => {
    if (!data || !data.detay) return;
    exportCsv(data.detay.map(d => ({
      Tarih: d.tarih || '',
      Malzeme: d.malzeme_ad || '',
      Lokasyon: d.lokasyon_ad || '',
      Miktar: d.miktar,
      Birim: d.birim || '',
      'Değer (TL)': d.deger,
      Kategori: d.neden_kategori_ad || '',
      Neden: d.neden || '',
    })), `zayi-raporu-${baslangic}-${bitis}.csv`);
  };

  return (
    <div>
      <Header title="Zayi / Fire Raporu" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Filtre Bar */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '16px 20px', boxShadow: shadows.md, marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginRight: '8px' }}>Hızlı:</span>
            {[
              { label: 'Bu Ay', fn: setThisMonth },
              { label: 'Gecen Ay', fn: setLastMonth },
              { label: 'Son 3 Ay', fn: () => setQuickRange(3) },
              { label: 'Son 6 Ay', fn: () => setQuickRange(6) },
            ].map(b => (
              <button key={b.label} onClick={b.fn} style={{
                padding: '5px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                background: colors.surface, cursor: 'pointer', fontSize: '12px', color: '#555',
                transition: 'all 0.2s'
              }}>{b.label}</button>
            ))}

            <div style={{ width: '1px', height: '24px', background: '#e0e0e0', margin: '0 8px' }} />

            <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px' }} />
            <span style={{ color: colors.textTertiary }}>-</span>
            <input type="date" value={bitis} onChange={e => setBitis(e.target.value)}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px' }} />

            <select value={lokasyonId} onChange={e => setLokasyonId(e.target.value)}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px', background: colors.surface }}>
              <option value="">Tüm Lokasyonlar</option>
              {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: colors.textTertiary }}>Yükleniyor...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '60px', color: styles.danger }}>{error}</div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: '60px', color: colors.textTertiary }}>Veri bulunamadı</div>
        ) : (
          <>
            {/* KPI Cards — TL primary (malzemeler farklı birimli, miktar toplamı anlamsız) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <KpiCard title="Toplam Zayi Değeri" value={formatTRY(data.kpis.toplam_deger || 0)} color={styles.danger} icon="💰" />
              <KpiCard title="Kayit Sayısı" value={data.kpis.kayit_sayisi || 0} color={styles.info} icon="📋" />
              <KpiCard title="En Cok Zayi Ürün" value={data.kpis.top_urun || '-'} color="#ff6b6b" icon="📊" />
            </div>

  {/*   **********************   EKLEME   ***************************   */}   
  
<div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md, marginBottom: '20px' }}>
  <FireZayiMaliyetChart rows={data.detay} productsSummary={data.by_product} />
</div>
      
  {/*   **********************   SON  ***************************   */}               

            {/* Charts 2x2 Grid — TL bazlı (birim-bağımsız, doğru karşılaştırma) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              {/* Top 10 Urun */}
              
              <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>En Çok Zayi Ürünler (TL)</h3>
                <HorizontalBarChart items={data.by_product} valueKey="toplam_deger" labelKey="malzeme_ad" color={styles.danger} />
              </div>

              {/* Lokasyon Bazli */}
              <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>Lokasyon Bazlı Zayi (TL)</h3>
                <HorizontalBarChart items={data.by_lokasyon} valueKey="toplam_deger" labelKey="lokasyon_ad" color={styles.info} />
              </div>

              {/* Neden Bazli — tanımlı neden kategorisi (master data) bazında gruplanır;
                  kategorisiz/legacy kayıtlar serbest-metin/"Belirtilmedi" kovasına düşer.
                  Bar renkleri kategori renginden gelir (neden_kategori_renk). */}
              <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>Zayi Neden Kategorisi Dağılımı (TL)</h3>
               <HorizontalBarChart
                  items={data.by_neden}
                  valueKey="toplam_deger"
                  labelKey="neden_kategori_ad"
                  colorMap={reasonColors}
                />
              </div>

              {/* Aylık Trend */}
              <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>Aylık Zayi Trendi (TL)</h3>
                <TrendChart items={data.monthly_trend} />
              </div>
            </div>

            {/* Detay Tablosu */}
            <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', color: '#333' }}>Detay ({(data.detay || []).length} kayit)</h3>
                <button onClick={exportCSV} style={{
                  padding: '6px 14px', border: `1px solid ${styles.primary}`, borderRadius: radius.sm,
                  background: colors.surface, color: styles.primary, cursor: 'pointer', fontSize: '12px', fontWeight: '600'
                }}>CSV Indir</button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt, borderBottom: `2px solid ${colors.border}` }}>
                      {['Tarih', 'Malzeme', 'Lokasyon', 'Miktar', 'Birim', 'Değer (TL)', 'Kategori', 'Neden'].map(h => (
                        <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#555' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.detay || []).map((d, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px' }}>{d.tarih}</td>
                        <td style={{ padding: '8px', fontWeight: '500' }}>{d.malzeme_ad}</td>
                        <td style={{ padding: '8px' }}>{d.lokasyon_ad}</td>
                        <td style={{ padding: '8px', fontWeight: '600', color: styles.danger }}>{formatSayi(d.miktar)}</td>
                        <td style={{ padding: '8px', color: '#888', fontSize: '12px' }}>{d.birim || '-'}</td>
                        <td style={{ padding: '8px' }}>{formatTRY(d.deger, { decimals: 2 })}</td>
                        <td style={{ padding: '8px' }}>
                          {d.neden_kategori_ad ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                              background: (d.neden_kategori_renk || colors.textTertiary) + '22',
                              color: d.neden_kategori_renk || colors.textSecondary,
                              border: `1px solid ${(d.neden_kategori_renk || colors.textTertiary) + '55'}`
                            }}>
                              {d.neden_kategori_ad}
                            </span>
                          ) : (
                            <span style={{ color: colors.textTertiary }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '8px', color: '#888' }}>{d.neden}</td>
                      </tr>
                    ))}
                    {(!data.detay || data.detay.length === 0) && (
                      <tr><td colSpan={8} style={{ padding: '20px', textAlign: 'center', color: colors.textTertiary }}>Kayit bulunamadı</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ZayiRaporuPage;
